import { motion } from "framer-motion";
import {
  AlertTriangle,
  Activity,
  Heart,
  Bot,
  Stethoscope,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import MockFrame from "./MockFrame";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: 0.08 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PRIORITY = {
  P1: { dot: "#fb7185", bg: "rgba(251, 113, 133, 0.12)", border: "rgba(251, 113, 133, 0.3)", text: "rgba(254, 205, 211, 0.95)" },
  P2: { dot: "#fbbf24", bg: "rgba(251, 191, 36, 0.10)", border: "rgba(251, 191, 36, 0.28)", text: "rgba(253, 230, 138, 0.95)" },
  P3: { dot: "#60a5fa", bg: "rgba(96, 165, 250, 0.10)", border: "rgba(96, 165, 250, 0.28)", text: "rgba(191, 219, 254, 0.95)" },
};

const tickets = [
  { p: "P1", title: "EHR sync latency", dept: "Cardiology · Ward 4", sla: "12m left", assignee: "MR" },
  { p: "P2", title: "Infusion pump offline", dept: "Pediatrics ICU", sla: "1h 24m", assignee: "JL" },
  { p: "P2", title: "VPN auth failures", dept: "Radiology · MRI-2", sla: "2h 10m", assignee: "AS" },
  { p: "P3", title: "Print queue stalled", dept: "Admin · Billing", sla: "5h 30m", assignee: "TW" },
];

function Stat({ icon: Icon, label, value, sub, accent, tone = "default", i }) {
  const valueColor =
    tone === "good" ? "#34d399" : tone === "warn" ? "#fbbf24" : accent.light;
  return (
    <motion.div
      variants={fadeUp}
      custom={i}
      className="rounded-xl border border-white/5 bg-white/[0.025] p-3"
    >
      <div className="flex items-center justify-between">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-lg ring-1"
          style={{ backgroundColor: accent.bg, color: accent.primary, borderColor: accent.border }}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
        </span>
        <span
          className="text-[9px] font-bold uppercase tracking-[0.1em]"
          style={{ color: valueColor }}
        >
          {sub}
        </span>
      </div>
      <p className="mt-2.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white/40">
        {label}
      </p>
      <p className="mt-0.5 text-[1.05rem] font-bold leading-none text-white tabular-nums">
        {value}
      </p>
    </motion.div>
  );
}

function Donut({ pct, accent }) {
  const radius = 24;
  const circ = 2 * Math.PI * radius;
  return (
    <div className="relative flex h-[68px] w-[68px] shrink-0 items-center justify-center">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 60 60" aria-hidden>
        <circle
          cx="30"
          cy="30"
          r={radius}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="5"
          fill="none"
        />
        <motion.circle
          cx="30"
          cy="30"
          r={radius}
          stroke={accent.primary}
          strokeWidth="5"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          whileInView={{ strokeDashoffset: circ * (1 - pct / 100) }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.3 }}
          style={{ filter: `drop-shadow(0 0 6px ${accent.glow})` }}
        />
      </svg>
      <div className="relative text-center">
        <p
          className="text-[14px] font-bold leading-none tabular-nums"
          style={{ color: accent.light }}
        >
          {pct}%
        </p>
        <p className="mt-0.5 text-[8px] font-semibold uppercase tracking-[0.1em] text-white/35">
          SLA
        </p>
      </div>
    </div>
  );
}

function VolumeBars({ accent }) {
  const bars = [38, 52, 44, 64, 78, 92, 70, 84, 58, 46, 60, 50];
  return (
    <div className="flex h-[60px] items-end gap-[3px]">
      {bars.map((h, i) => (
        <motion.span
          key={i}
          initial={{ height: 0, opacity: 0 }}
          whileInView={{ height: `${h}%`, opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{
            duration: 0.55,
            delay: 0.35 + i * 0.035,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="flex-1 rounded-t-[2px]"
          style={{
            background: `linear-gradient(180deg, ${accent.primary}, ${accent.primary}55)`,
          }}
        />
      ))}
    </div>
  );
}

export default function VeloDeskVisual({ accent }) {
  return (
    <MockFrame
      domain="velodesk.velozent.com / operations"
      accent={accent}
      tabs={["Queue", "Incidents", "SLA", "Compliance"]}
      activeTab={1}
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        className="space-y-3"
      >
        <div className="grid grid-cols-3 gap-2.5">
          <Stat
            icon={Activity}
            label="Open tickets"
            value="47"
            sub="↓ 12%"
            tone="good"
            accent={accent}
            i={0}
          />
          <Stat
            icon={ShieldCheck}
            label="SLA compliance"
            value="96.4%"
            sub="On track"
            tone="good"
            accent={accent}
            i={1}
          />
          <Stat
            icon={TrendingUp}
            label="MTTR"
            value="2.3h"
            sub="↓ 18% wow"
            tone="good"
            accent={accent}
            i={2}
          />
        </div>

        <motion.div
          variants={fadeUp}
          custom={3}
          className="rounded-xl border border-white/5 bg-white/[0.025]"
        >
          <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
              Active incidents
            </p>
            <span
              className="inline-flex items-center gap-1 text-[9.5px] font-bold"
              style={{ color: accent.light }}
            >
              <Bot className="h-3 w-3" />
              AI-routed · 18s avg
            </span>
          </div>
          <ul className="divide-y divide-white/5">
            {tickets.map((t, i) => {
              const p = PRIORITY[t.p];
              return (
                <motion.li
                  key={t.title}
                  variants={fadeUp}
                  custom={4 + i}
                  className="flex items-center gap-2.5 px-3 py-2"
                >
                  <span
                    className="inline-flex items-center gap-1 rounded-md border px-1.5 py-[2px] text-[9.5px] font-bold tabular-nums"
                    style={{
                      borderColor: p.border,
                      backgroundColor: p.bg,
                      color: p.text,
                    }}
                  >
                    <span
                      className="h-1 w-1 rounded-full"
                      style={{ backgroundColor: p.dot }}
                    />
                    {t.p}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate text-[11px] font-semibold text-white/90">
                      <AlertTriangle
                        className="h-2.5 w-2.5 shrink-0"
                        style={{ color: p.dot }}
                      />
                      {t.title}
                    </p>
                    <p className="flex items-center gap-1 truncate text-[9.5px] text-white/45">
                      <Heart className="h-2.5 w-2.5" style={{ color: accent.primary }} />
                      {t.dept}
                    </p>
                  </div>
                  <span className="hidden text-[10px] font-medium tabular-nums text-white/55 sm:inline">
                    {t.sla}
                  </span>
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ring-1"
                    style={{
                      backgroundColor: accent.bg,
                      color: accent.light,
                      borderColor: accent.border,
                    }}
                  >
                    {t.assignee}
                  </span>
                </motion.li>
              );
            })}
          </ul>
        </motion.div>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-5">
          <motion.div
            variants={fadeUp}
            custom={8}
            className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.025] p-3 sm:col-span-2"
          >
            <Donut pct={96} accent={accent} />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                Compliance posture
              </p>
              <p className="mt-1 text-[10.5px] leading-snug text-white/65">
                HIPAA &amp; HITRUST<br />
                <span style={{ color: accent.light }} className="font-semibold">
                  All audits passing
                </span>
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={9}
            className="rounded-xl border border-white/5 bg-white/[0.025] p-3 sm:col-span-3"
          >
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                Ticket volume · last 12h
              </p>
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold tabular-nums"
                style={{ color: accent.light }}
              >
                <Stethoscope className="h-3 w-3" />
                4 wards
              </span>
            </div>
            <div className="mt-2">
              <VolumeBars accent={accent} />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </MockFrame>
  );
}
